// src/services/licensingService.ts
// Character Licensing operations via Figryne Backend API

import { config } from '../config';
import { auth } from './firebase';
import { CharacterLicensing } from '../types/character';

// Helper to get auth token
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
}

// API Response types
export interface LicensingSettings {
  enabled: boolean;
  royaltyPercentage: number;  // 1-20%
  requiresAttribution: boolean;
  customTerms?: string;
}

export interface LicensingResponse {
  enabled: boolean;
  royaltyPercentage: number;
  requiresAttribution: boolean;
  customTerms?: string;
  updatedAt?: string;
}

export interface LicensableCharacter {
  id: string;
  name: string;
  thumbnailUrl?: string;
  ownerId: string;
  ownerName?: string;
  ownerUsername?: string;
  ownerAvatar?: string;
  licensing: LicensingResponse;
}

export interface LicensableCharacterListResponse {
  characters: LicensableCharacter[];
  hasMore: boolean;
}

// ========== Licensing Settings Operations ==========

/**
 * Update licensing settings for a character
 * @param characterId - Character ID
 * @param settings - Licensing settings
 */
export const updateLicensingSettings = async (
  characterId: string,
  settings: LicensingSettings
): Promise<LicensingResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${config.apiBaseUrl}/api/orifoli/characters/${characterId}/licensing`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update licensing settings: ${errorText}`);
  }

  return response.json();
};

/**
 * Get licensing settings for a character (public)
 * @param characterId - Character ID
 */
export const getLicensingSettings = async (
  characterId: string
): Promise<LicensingResponse | null> => {
  try {
    const response = await fetch(
      `${config.apiBaseUrl}/api/orifoli/characters/${characterId}/licensing`,
      {
        method: 'GET',
      }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to get licensing settings');
    }

    return response.json();
  } catch {
    return null;
  }
};

// ========== Licensable Characters Search ==========

export interface LicensableSearchParams {
  search?: string;
  tags?: string[];
  limit?: number;
  before?: string;
}

/**
 * List characters available for licensing
 * @param params - Search parameters
 */
export const listLicensableCharacters = async (
  params: LicensableSearchParams = {}
): Promise<LicensableCharacterListResponse> => {
  const token = await getAuthToken();

  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.tags && params.tags.length > 0) queryParams.append('tags', params.tags.join(','));
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.before) queryParams.append('before', params.before);

  const url = `${config.apiBaseUrl}/api/orifoli/characters/licensable${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to list licensable characters: ${errorText}`);
  }

  return response.json();
};

// ========== Helper to convert API response to CharacterLicensing type ==========

export function apiToCharacterLicensing(response: LicensingResponse): CharacterLicensing {
  return {
    enabled: response.enabled,
    royaltyPercentage: response.royaltyPercentage,
    requiresAttribution: response.requiresAttribution,
    customTerms: response.customTerms,
    updatedAt: response.updatedAt
      ? { toDate: () => new Date(response.updatedAt!) } as any
      : { toDate: () => new Date() } as any,
  };
}
